import { z } from "zod"
import { Provider } from "./channels.js"
import { Id } from "./ids.js"

// ---- Fields ----

export const FieldType = z.enum(["text", "number", "date", "single_select"])
export type FieldType = z.infer<typeof FieldType>
export const FIELD_TYPE_LABELS: Record<FieldType, string> = { text: "Text", number: "Number", date: "Date", single_select: "Single select" }

export const FieldKey = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, "Use a lowercase key with letters, numbers and underscores")

export const FieldDefinition = z.object({
  id: Id.nullable(),
  key: z.string(),
  label: z.string(),
  type: FieldType,
  options: z.array(z.string()),
  origin: z.enum(["standard", "workspace"]),
})
export type FieldDefinition = z.infer<typeof FieldDefinition>

/** Columns on the contact record. They are always present and cannot be redefined. */
export const STANDARD_FIELDS: FieldDefinition[] = [
  { id: null, key: "display_name", label: "Full name", type: "text", options: [], origin: "standard" },
  { id: null, key: "first_name", label: "First name", type: "text", options: [], origin: "standard" },
  { id: null, key: "email", label: "Email address", type: "text", options: [], origin: "standard" },
  { id: null, key: "city", label: "City", type: "text", options: [], origin: "standard" },
  { id: null, key: "timezone", label: "Timezone", type: "text", options: [], origin: "standard" },
]

export const CreateFieldInput = z.object({
  label: z.string().trim().min(1).max(60),
  key: FieldKey,
  type: FieldType,
  options: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
})
export type CreateFieldInput = z.infer<typeof CreateFieldInput>

// ---- Tags ----

export const Tag = z.object({ id: Id, name: z.string(), contactCount: z.number().int().optional() })
export type Tag = z.infer<typeof Tag>

// ---- Consent ----

export const PermissionState = z.enum(["granted", "revoked", "unknown"])
export type PermissionState = z.infer<typeof PermissionState>
export const PERMISSION_LABELS: Record<PermissionState, string> = { granted: "Permitted", revoked: "Opted out", unknown: "Unknown" }

export const ConsentEvent = z.object({
  id: Id,
  provider: Provider,
  identityId: Id.nullable(),
  purpose: z.string(),
  status: z.enum(["granted", "revoked"]),
  source: z.string(),
  evidence: z.string().nullable(),
  recordedByName: z.string(),
  occurredAt: z.iso.datetime(),
})
export type ConsentEvent = z.infer<typeof ConsentEvent>

export const RecordConsentInput = z.object({
  provider: Provider,
  identityId: Id.optional(),
  status: z.enum(["granted", "revoked"]),
  source: z.string().trim().min(1).max(80),
  evidence: z.string().trim().max(500).optional(),
  occurredAt: z.iso.datetime().optional(),
})
export type RecordConsentInput = z.infer<typeof RecordConsentInput>

// ---- Contacts ----

export const IdentitySummary = z.object({
  id: Id,
  provider: Provider,
  scope: z.enum(["messaging", "comments"]),
  handle: z.string().nullable(),
  displayName: z.string().nullable(),
  connectionName: z.string(),
  externalActorId: z.string(),
  marketing: PermissionState,
  /** Support replies are allowed until this instant (24h after the last inbound message), or null. */
  supportWindowEndsAt: z.iso.datetime().nullable(),
  conversationId: Id.nullable(),
  lastSeenAt: z.iso.datetime(),
})
export type IdentitySummary = z.infer<typeof IdentitySummary>

export const ContactSummary = z.object({
  id: Id,
  displayName: z.string(),
  email: z.email().nullable(),
  /** What to show under the name: email, else a handle, else nothing. */
  detail: z.string().nullable(),
  identityLabel: z.string(),
  tags: z.array(Tag),
  marketing: z.record(Provider, PermissionState),
  lastActiveAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
})
export type ContactSummary = z.infer<typeof ContactSummary>

export const Note = z.object({ id: Id, body: z.string(), authorName: z.string(), createdAt: z.iso.datetime() })
export type Note = z.infer<typeof Note>

export const ActivityItem = z.object({
  id: z.string(),
  kind: z.enum(["message", "comment", "consent", "identity", "import", "note", "data_request"]),
  title: z.string(),
  detail: z.string(),
  body: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  conversationId: Id.nullable(),
})
export type ActivityItem = z.infer<typeof ActivityItem>

export const ContactDetail = ContactSummary.extend({
  firstName: z.string().nullable(),
  city: z.string().nullable(),
  timezone: z.string().nullable(),
  fields: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  referenceHandles: z.record(z.string(), z.string()),
  source: z.string(),
  identities: z.array(IdentitySummary),
  notes: z.array(Note),
  consentEvents: z.array(ConsentEvent),
  activity: z.array(ActivityItem),
})
export type ContactDetail = z.infer<typeof ContactDetail>

export const ContactsQuery = z.object({
  q: z.string().trim().max(120).optional(),
  tagId: Id.optional(),
  permission: PermissionState.optional(),
  segmentId: Id.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type ContactsQuery = z.infer<typeof ContactsQuery>

export const ContactsPage = z.object({ contacts: z.array(ContactSummary), nextCursor: z.string().nullable(), total: z.number().int() })
export type ContactsPage = z.infer<typeof ContactsPage>

export const ContactsStats = z.object({ people: z.number().int(), identities: z.number().int(), segments: z.number().int(), tags: z.array(Tag) })
export type ContactsStats = z.infer<typeof ContactsStats>

export const UpdateContactInput = z.object({
  displayName: z.string().trim().min(1).max(120).optional(),
  firstName: z.string().trim().max(60).nullable().optional(),
  email: z.email().toLowerCase().nullable().optional(),
  city: z.string().trim().max(80).nullable().optional(),
  timezone: z.string().trim().max(60).nullable().optional(),
  fields: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).optional(),
})
export type UpdateContactInput = z.infer<typeof UpdateContactInput>

export const TagContactsInput = z.object({ contactIds: z.array(Id).min(1).max(500), tagId: Id.optional(), tagName: z.string().trim().min(1).max(40).optional() })
export type TagContactsInput = z.infer<typeof TagContactsInput>

export const AddNoteInput = z.object({ body: z.string().trim().min(1).max(4000) })

// ---- Segments ----

const withinDays = z.number().int().min(1).max(365)

export const SegmentCondition = z.discriminatedUnion("type", [
  z.object({ type: z.literal("tag"), op: z.enum(["has", "not_has"]), tagId: Id }),
  z.object({ type: z.literal("field"), key: z.string().min(1).max(40), op: z.enum(["eq", "neq", "contains", "is_set", "is_not_set", "gt", "lt"]), value: z.union([z.string().max(200), z.number()]).optional() }),
  z.object({ type: z.literal("email"), op: z.enum(["is_set", "is_not_set"]) }),
  z.object({ type: z.literal("last_interaction"), provider: z.enum(["instagram", "messenger", "any"]), withinDays }),
  z.object({ type: z.literal("created"), withinDays }),
  z.object({ type: z.literal("conversation"), op: z.enum(["has_open", "has_any"]) }),
  z.object({ type: z.literal("source"), importId: Id }),
])
export type SegmentCondition = z.infer<typeof SegmentCondition>

export interface SegmentGroup {
  match: "all" | "any"
  conditions: Array<SegmentCondition | SegmentGroup>
}
export const SegmentGroup: z.ZodType<SegmentGroup> = z.lazy(() =>
  z.object({ match: z.enum(["all", "any"]), conditions: z.array(z.union([SegmentCondition, SegmentGroup])).min(1).max(20) })
)

export const Segment = z.object({
  id: Id,
  name: z.string(),
  description: z.string(),
  definition: SegmentGroup,
  summary: z.string(),
  matchCount: z.number().int().nullable(),
  previewedAt: z.iso.datetime().nullable(),
  usedIn: z.array(z.string()),
  updatedAt: z.iso.datetime(),
})
export type Segment = z.infer<typeof Segment>

export const SaveSegmentInput = z.object({ name: z.string().trim().min(1).max(80), description: z.string().trim().max(200).default(""), definition: SegmentGroup })
export type SaveSegmentInput = z.infer<typeof SaveSegmentInput>

export const SegmentPreview = z.object({ count: z.number().int(), sample: z.array(ContactSummary), previewedAt: z.iso.datetime() })
export type SegmentPreview = z.infer<typeof SegmentPreview>

// ---- Imports ----

/** Targets a CSV column may map to. Consent-like columns may only become a source note or be ignored. */
export const IMPORT_TARGETS = ["display_name", "first_name", "email", "city", "timezone", "instagram_handle", "tags", "source_note", "ignore"] as const
export const ImportTarget = z.string().regex(/^(display_name|first_name|email|city|timezone|instagram_handle|tags|source_note|ignore|field:[a-z][a-z0-9_]*)$/)

export const ImportStatus = z.enum(["mapping", "reviewed", "completed", "failed"])
export const ImportDecision = z.enum(["create", "update", "skip", "error"])

export const ContactImport = z.object({
  id: Id,
  fileName: z.string(),
  status: ImportStatus,
  columns: z.array(z.string()),
  samples: z.record(z.string(), z.string()),
  mapping: z.record(z.string(), z.string()),
  /** Columns whose name suggests consent; they cannot map to a contact field. */
  restrictedColumns: z.array(z.string()),
  options: z.object({ reviewMatches: z.boolean() }),
  rowCount: z.number().int(),
  summary: z.object({ create: z.number().int(), update: z.number().int(), skip: z.number().int(), error: z.number().int() }).nullable(),
  createdAt: z.iso.datetime(),
})
export type ContactImport = z.infer<typeof ContactImport>

export const UpdateImportInput = z.object({ mapping: z.record(z.string(), ImportTarget), options: z.object({ reviewMatches: z.boolean() }) })
export type UpdateImportInput = z.infer<typeof UpdateImportInput>

export const ImportRow = z.object({
  rowNumber: z.number().int(),
  name: z.string(),
  email: z.string().nullable(),
  match: z.string(),
  decision: ImportDecision,
  reason: z.string().nullable(),
  changes: z.array(z.string()),
})
export type ImportRow = z.infer<typeof ImportRow>

export const ImportRowsPage = z.object({ rows: z.array(ImportRow), total: z.number().int() })

// ---- Data requests ----

export const DataRequestType = z.enum(["export", "delete"])
export const DataRequest = z.object({
  id: Id,
  /** Null once the person has been deleted. */
  contactId: Id.nullable(),
  contactName: z.string(),
  type: DataRequestType,
  status: z.enum(["recorded", "processing", "completed", "failed"]),
  verified: z.boolean(),
  requestedByName: z.string(),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
})
export type DataRequest = z.infer<typeof DataRequest>

export const CreateDataRequestInput = z.object({ contactId: Id, type: DataRequestType, verified: z.literal(true), note: z.string().trim().max(500).optional() })
export type CreateDataRequestInput = z.infer<typeof CreateDataRequestInput>
